import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Card,
    CardMedia,
    CardContent,
    Typography,
    IconButton,
    Button,
    CircularProgress,
    Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import { useSnackbar } from 'notistack';
import adminApi from '../../../services/adminApi';
import UploadModal from './UploadModal';
import { formatBytes } from '../../../utils/format';

// Get base URL for images from environment or default
const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

interface Asset {
    _id: string;
    filename: string;
    originalName: string;
    url: string;
    thumbnailUrl: string;
    size: number;
    dimensions: {
        width: number;
        height: number;
    };
    createdAt: string;
    usedIn: {
        couponId: string;
        couponName: string;
    }[];
}

interface FailedImages {
    [key: string]: boolean;
}

const AssetList: React.FC = () => {
    const [assets, setAssets] = useState<Asset[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [failedImages, setFailedImages] = useState<FailedImages>({});
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        fetchAssets();
    }, []);

    const fetchAssets = async () => {
        try {
            console.log('Fetching assets...');
            const response = await adminApi.getAssets();
            console.log('Assets response:', response.data);
            if (response.data.data && response.data.data.assets) {
                setAssets(response.data.data.assets);
            } else {
                console.error('Unexpected response format:', response.data);
                enqueueSnackbar('Invalid response format from server', { variant: 'error' });
            }
        } catch (error: any) {
            console.error('Error fetching assets:', error.response || error);
            enqueueSnackbar(error.response?.data?.message || 'Failed to load assets', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await adminApi.deleteAsset(id);
            setAssets(assets.filter(asset => asset._id !== id));
            enqueueSnackbar('Asset deleted successfully', { variant: 'success' });
        } catch (error: any) {
            console.error('Error deleting asset:', error.response || error);
            enqueueSnackbar(error.response?.data?.message || 'Failed to delete asset', { 
                variant: 'error' 
            });
        }
    };

    const handleUploadSuccess = (newAsset: Asset) => {
        setAssets([newAsset, ...assets]);
        setUploadModalOpen(false);
        enqueueSnackbar('Asset uploaded successfully', { variant: 'success' });
    };

    const handleImageError = (assetId: string) => {
        console.error('Image failed to load:', assetId);
        setFailedImages(prev => ({ ...prev, [assetId]: true }));
    };

    const getImageUrl = (path: string) => {
        // Remove any leading slashes and combine with base URL
        const cleanPath = path.replace(/^\/+/, '');
        const url = `${BASE_URL}/${cleanPath}`;
        console.log('Generated image URL:', url);
        
        // Add timestamp to prevent caching in development
        if (process.env.NODE_ENV === 'development') {
            return `${url}?t=${new Date().getTime()}`;
        }
        return url;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" component="h1">
                    Assets ({assets.length})
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => setUploadModalOpen(true)}
                >
                    Upload New
                </Button>
            </Box>

            {assets.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="textSecondary">
                        No assets found. Click "Upload New" to add some images.
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {assets.map((asset) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={asset._id}>
                            <Card>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        height: 140,
                                        backgroundColor: '#f5f5f5',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    {failedImages[asset._id] ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                color: 'text.secondary'
                                            }}
                                        >
                                            <ImageIcon sx={{ fontSize: 40, mb: 1 }} />
                                            <Typography variant="caption">Image not available</Typography>
                                        </Box>
                                    ) : (
                                        <CardMedia
                                            component="img"
                                            height="140"
                                            image={getImageUrl(asset.thumbnailUrl)}
                                            alt={asset.originalName}
                                            sx={{
                                                objectFit: 'contain',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%'
                                            }}
                                            onError={() => handleImageError(asset._id)}
                                        />
                                    )}
                                </Box>
                                <CardContent>
                                    <Typography noWrap title={asset.originalName}>
                                        {asset.originalName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {formatBytes(asset.size)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {asset.dimensions.width} × {asset.dimensions.height}
                                    </Typography>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                                        <Typography variant="body2" color="text.secondary">
                                            Used in: {asset.usedIn.length} coupon(s)
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(asset._id)}
                                            disabled={asset.usedIn.length > 0}
                                            title={
                                                asset.usedIn.length > 0
                                                    ? 'Cannot delete: asset is in use'
                                                    : 'Delete asset'
                                            }
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            <UploadModal
                open={uploadModalOpen}
                onClose={() => setUploadModalOpen(false)}
                onSuccess={handleUploadSuccess}
            />
        </Box>
    );
};

export default AssetList;
